<template>
  <div class="tw-space-y-8">
    <!-- Loading indicator -->
    <div v-if="isLoading" class="tw-text-center tw-py-8">
      <div
        class="tw-spinner tw-border-4 tw-border-gray-200 tw-border-t-blue-500 tw-rounded-full tw-w-8 tw-h-8 tw-animate-spin tw-mx-auto"
      ></div>
      <p class="tw-text-gray-600 tw-mt-2">{{ t('common.loading') }}</p>
    </div>

    <!-- Stepper Header -->
    <Card v-if="!isLoading">
      <CardContent class="tw-p-6 tw-pt-6">
        <Stepper :current-step="currentStep" :total-steps="steps.length">
          <Step
            v-for="step in steps"
            :key="step.id"
            :step="step.id"
            :title="step.title"
            :description="step.description"
            :is-active="currentStep === step.id"
            :is-completed="completedSteps.has(step.id)"
            class="tw-cursor-pointer"
            @click="handleStepClick"
          />
        </Stepper>
      </CardContent>
    </Card>

    <!-- Step Content -->
    <div v-if="!isLoading" class="tw-min-h-[600px]">
      <!-- Step 1: Basic Information -->
      <MainDataForm
        ref="mainDataRef"
        v-if="currentStep === 1"
        :data="getMainDataFromForm()"
        :is-edit="false"
        @on-change="handleMainDataChange"
        @next="handleNext"
        @finish="handleFinish"
      />

      <!-- Step 2: Team Assignment -->
      <TeamAssignmentForm
        v-else-if="currentStep === 2"
        :data="{ team: formData.team }"
        :users="allUsers"
        :is-edit="false"
        @change="handleTeamChange"
        @previous="handlePrevious"
        @next="handleNext"
        @finish="handleFinish"
      />

      <!-- Step 3: Form Configuration -->
      <CustomerForms
        v-else-if="currentStep === 3"
        :data="{ forms: formData.forms }"
        :is-edit="false"
        @change="handleFormsChange"
        @previous="handlePrevious"
        @next="handleNext"
        @finish="handleFinish"
      />

      <!-- Step 4: Legal Terms -->
      <Legals
        v-else-if="currentStep === 4"
        :data="{ legals: formData.legals }"
        :is-edit="false"
        @change="handleLegalsChange"
        @previous="handlePrevious"
        @finish="handleFinish"
      />
    </div>

    <!-- TeamAssignmentForm now owns invite flow -->
  </div>
</template>

<script setup lang="ts">
import { Card, CardContent } from '@/components/ui/card';
import { Step, Stepper } from '@/components/ui/stepper';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import router from '@/router';
import { useCustomerStore } from '@/stores/customer.store';
import type { CreateCustomerPayload } from '@/types/customer';
import { FinancialLimitFrequency } from '@/types/customer';
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import CustomerForms from '../Forms/CustomerForms.vue';
import Legals from '../Forms/Legals.vue';
import MainDataForm from '../Forms/MainDataForm.vue';
import TeamAssignmentForm from '../Forms/TeamAssignmentForm.vue';

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const customerStore = useCustomerStore();

// Shape returned by API for users (used in TeamAssignmentForm)
interface ApiUser {
  id: string | number;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
  roles?: string[];
  active: boolean;
  is_admin: boolean;
}

// Customer interface removed - using CreateCustomerPayload directly

// Props and emits removed - component now handles creation internally

const isLoading = ref(false);
const allUsers = ref<ApiUser[]>([]);

const steps = [
  {
    id: 1,
    title: t('customer_management.titles.main_data'),
    description: t('customer_management.descriptions.main_data'),
  },
  {
    id: 2,
    title: t('customer_management.assign_team.title'),
    description: t('customer_management.descriptions.team_assignment'),
  },
  {
    id: 3,
    title: t('customer_management.titles.order_forms'),
    description: t('customer_management.descriptions.form_configuration'),
  },
  {
    id: 4,
    title: t('customer_management.titles.legals'),
    description: t('customer_management.descriptions.legal_terms'),
  },
];

const toast = useToast();
const currentStep = ref(1);
const completedSteps = ref(new Set<number>());
// Invite is handled within TeamAssignmentForm
const mainDataRef = ref<any>(null);

const formData = reactive({
  name: '',
  legalName: '',
  customerLimit: 0,
  automaticApprovalLimit: 0,
  financialLimitFrequency: FinancialLimitFrequency.MONTHLY,
  address: '',
  email: '',
  phone: '',
  logo: '' as string | File,
  restrictMode: false,
  team: {
    purchasers: [] as (string | number)[],
    orderClearingGroups: [
      { display_order: 1, name: 'Group A', assignees: [] as (number | string)[] },
      { display_order: 2, name: 'Group B', assignees: [] as (number | string)[] },
      { display_order: 3, name: 'Group C', assignees: [] as (number | string)[] },
      { display_order: 4, name: 'Group D', assignees: [] as (number | string)[] },
    ],
    payers: [] as (string | number)[],
    paymentClearingGroups: [
      { display_order: 1, name: 'Group A', assignees: [] as (number | string)[] },
      { display_order: 2, name: 'Group B', assignees: [] as (number | string)[] },
      { display_order: 3, name: 'Group C', assignees: [] as (number | string)[] },
      { display_order: 4, name: 'Group D', assignees: [] as (number | string)[] },
    ],
    approvers: [] as { userId: string | number; limit: number }[],
    viewers: [] as (string | number)[],
  },
  forms: {
    orderRequired: [] as number[],
    orderOptional: [] as number[],
    paymentRequired: [] as number[],
    paymentOptional: [] as number[],
  },
  legals: {
    legalTerms: '',
    termsAndConditions: '',
  },
});

const handleNext = async () => {
  // Mark current step as completed and move to next step
  completedSteps.value.add(currentStep.value);

  if (currentStep.value < steps.length) {
    currentStep.value += 1;
  }
};

// Build the complete customer payload for the grouped API
const buildCustomerPayload = (): CreateCustomerPayload => {
  // Build user assignments array
  const userAssignments: any[] = [];

  // Add purchasers
  formData.team.purchasers.forEach(userId => {
    userAssignments.push({
      user: userId,
      user_role: 'PURCHASER',
      is_role_default: false,
      approver_limit: null,
    });
  });

  // Add payers
  formData.team.payers.forEach(userId => {
    userAssignments.push({
      user: userId,
      user_role: 'PAYER',
      is_role_default: false,
      approver_limit: null,
    });
  });

  // Add approvers with their limits
  formData.team.approvers.forEach(approver => {
    userAssignments.push({
      user: approver.userId,
      user_role: 'APPROVER',
      is_role_default: false,
      approver_limit: approver.limit.toString(),
    });
  });

  // Add viewers
  formData.team.viewers.forEach(userId => {
    userAssignments.push({
      user: userId,
      user_role: 'VIEWER',
      is_role_default: false,
      approver_limit: null,
    });
  });

  // Build order fields array
  const orderFields: any[] = [];
  formData.forms.orderRequired.forEach(fieldId => {
    orderFields.push({
      field: fieldId,
      is_mandatory: true,
    });
  });
  formData.forms.orderOptional.forEach(fieldId => {
    orderFields.push({
      field: fieldId,
      is_mandatory: false,
    });
  });

  // Build payment fields array
  const paymentFields: any[] = [];
  formData.forms.paymentRequired.forEach(fieldId => {
    paymentFields.push({
      field: fieldId,
      is_mandatory: true,
    });
  });
  formData.forms.paymentOptional.forEach(fieldId => {
    paymentFields.push({
      field: fieldId,
      is_mandatory: false,
    });
  });

  // Build clearing groups payload
  const orderClearingGroups = formData.team.orderClearingGroups.map(g => ({
    display_order: g.display_order,
    name: g.name,
    assignees: g.assignees as number[],
  }));

  const paymentClearingGroups = formData.team.paymentClearingGroups.map(g => ({
    display_order: g.display_order,
    name: g.name,
    assignees: g.assignees as number[],
  }));

  return {
    name: formData.name,
    legal_name: formData.legalName,
    customer_limit: formData.customerLimit,
    auto_approval_limit: formData.automaticApprovalLimit,
    financial_limit_frequency: formData.financialLimitFrequency,
    email: formData.email,
    phone_number: formData.phone,
    address: formData.address,
    is_order_and_payment_restrict: formData.restrictMode,
    legal_notes: formData.legals.legalTerms,
    terms_and_conditions: formData.legals.termsAndConditions,
    user_assignments: userAssignments,
    order_fields: orderFields,
    payment_fields: paymentFields,
    order_clearing_groups: orderClearingGroups,
    payment_clearing_groups: paymentClearingGroups,
  };
};

const handlePrevious = () => {
  if (currentStep.value > 1) {
    currentStep.value -= 1;
  }
};

const handleStepClick = async (step: number) => {
  // Allow backward navigation freely
  if (step < currentStep.value) {
    currentStep.value = step;
    return;
  }
  // For forward navigation, validate current step
  if (currentStep.value === 1) {
    // Trigger child validation to surface inline errors
    if (mainDataRef.value?.validate) {
      const ok = await mainDataRef.value.validate();
      if (!ok) return;
    }
    handleNext();
  } else {
    handleNext();
  }
};

const handleFinish = async () => {
  isLoading.value = true;

  try {
    // Step 1: Create customer with all collected data (excluding logo)
    const customerPayload = buildCustomerPayload();
    const customerResponse = await customerStore.createCustomer(customerPayload);

    // Step 2: Upload logo if provided
    if (formData.logo && typeof formData.logo === 'object' && formData.logo instanceof File) {
      try {
        const logoData = {
          avatar: formData.logo,
        };
        await customerStore.updateMainData(logoData, customerResponse.id);
      } catch (logoError) {
        console.warn('Failed to upload logo, but customer was created:', logoError);
        // Don't fail the entire process if logo upload fails
        toast.warning(t('customer_management.messages.logo_upload_failed'));
      }
    }

    // Show success message
    toast.success(t('customer_management.messages.customer_created_successfully'));

    // Navigate to customer details page
    router.push({ name: 'view-customer', params: { id: customerResponse.id } });
  } catch (error: any) {
    console.error('Failed to complete customer creation:', error);
    toast.error(t('customer_management.messages.customer_creation_failed'));
  } finally {
    isLoading.value = false;
  }
};

// Component event handlers
const getMainDataFromForm = () => {
  return {
    name: formData.name,
    legalName: formData.legalName,
    customerLimit: formData.customerLimit,
    automaticApprovalLimit: formData.automaticApprovalLimit,
    financialLimitFrequency: formData.financialLimitFrequency,
    address: formData.address,
    email: formData.email,
    phone: formData.phone,
    logo: formData.logo,
    restrictMode: formData.restrictMode,
  };
};

const handleMainDataChange = (updates: any) => {
  Object.assign(formData, updates);
};

const handleTeamChange = (updates: any) => {
  Object.assign(formData.team, updates.team);
};

// Invitation-related methods removed; handled in TeamAssignmentForm

const handleFormsChange = (updates: any) => {
  Object.assign(formData.forms, updates.forms);
};

const handleLegalsChange = (updates: any) => {
  Object.assign(formData.legals, updates.legals);
};

// Initialize component - load users for team assignment
const initializeComponent = async () => {
  // Load users for team assignment
  try {
    allUsers.value = await customerStore.getUsers();
  } catch (error) {
    console.error('Failed to load users:', error);
    toast.error('Failed to load users');
  }

  // Set breadcrumbs
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.customer_listing',
      to: { name: 'customers' },
    },
    {
      labelKey: 'customer_management.actions.create_customer',
    },
  ]);
};

// Edit mode functionality removed - now only creating new customers

onMounted(() => {
  initializeComponent();
});
</script>
